/**
 * AgentHarnessTabBody — variant dispatcher for the unified "Harness" tab.
 *
 * The Harness tab hosts the variant-specific platform configuration for the
 * agent — the sandbox for Coding and project/deployment for Foundry. (Hosted
 * agents fold their configuration into the Parameters tab instead; see
 * HostedAgentConfigPanel.)
 *
 * Dispatches for the agent variants:
 *
 * - `coding`  → {@link AgentHarnessSelector} — sandbox harness, provisioning
 *                configuration, repo skills.
 * - `foundry` → {@link FoundryDetailsTab} — Foundry-hosted agent capabilities
 *                (tools, knowledge, memory, guardrails). *Wired in Phase 2.*
 * - `hosted`  → tab hidden by {@link useNodeEditorContext}'s `availableTabs`
 *                filter — hosted agents fold their platform configuration into
 *                the Parameters tab (see HostedAgentConfigPanel).
 * - `native`  → tab hidden by {@link useNodeEditorContext}'s `availableTabs`
 *                filter, so this dispatcher never sees it.
 *
 * The dispatcher is intentionally a single switch so a fifth variant is a
 * one-file change: add a case here and a component next to it. There is no
 * variant branching in the top-level {@link NodeEditorTabBody} switch.
 */

import type { NodeEditorContextValue } from './useNodeEditorContext';
import { AgentHarnessSelector } from '../sandbox/AgentHarnessSelector';
import { FoundryDetailsTab } from '../foundry/FoundryDetailsTab';

interface AgentHarnessTabBodyProps {
  ctx: NodeEditorContextValue;
}

export const AgentHarnessTabBody = ({ ctx }: AgentHarnessTabBodyProps) => {
  const {
    isCodingAgent,
    isFoundryAgent,
    connectionName,
    agentConnectionsConfig,
    parameterValues,
  } = ctx;

  if (isCodingAgent) {
    return <AgentHarnessSelector />;
  }

  if (isFoundryAgent) {
    return (
      <FoundryDetailsTab
        rawEndpoint={
          connectionName
            ? agentConnectionsConfig?.[connectionName]?.endpoint
            : undefined
        }
        connectionResourceId={
          connectionName
            ? agentConnectionsConfig?.[connectionName]?.resourceId
            : undefined
        }
        agentId={parameterValues['foundryAgentId'] as string | undefined}
        agentVersion={
          (parameterValues['foundryAgentVersion'] as 'v1' | 'v2' | undefined) ??
          'v2'
        }
      />
    );
  }

  return null;
};

AgentHarnessTabBody.displayName = 'AgentHarnessTabBody';
